import {
   formatClienteNombreCompleto,
   urlAbm,
   urlBsa,
   urlFlujoAgro,
   urlFlujoArt,
   urlPoliza,
} from '@/helpers';
import { formatDateOnly } from '@/helpers/dateHelpers';
import { httpInstance } from '@/clients/http.request';
import { useAuthHeaders } from '@/composables/useAuthHeaders';
import { showError } from '@/composables/useToast';
import { seccionUsaPosibilidadCierre } from '@/helpers/seccion';

export const masaSalarial = async ({ state, commit }) => {
   try {
      const poliza = state.poliza;
      const res = await httpInstance.put(`${urlFlujoArt}/masaSalarial`, {
         poliza: {
            id_poliza: poliza.id_poliza,
            masa_salarial: poliza.masa_salarial,
            capitas: poliza.capitas,
            id_compania_anterior: poliza.id_compania,
            id_art_poliza:
               poliza.id_art_poliza !== null ? poliza.id_art_poliza : undefined,
            alicuota_anterior: poliza.alicuota_anterior,
         },
      });
      commit('setArtPoliza', res.data.id_art_poliza);
      return res.data;
   } catch (error) {
      throw error;
   }
};

export const updateArtPoliza = async ({ state }, data) => {
   try {
      const res = await httpInstance.put(`${urlFlujoArt}/alicuota`, data);
   } catch (error) {
      throw error;
   }
};

export const loadPolicies = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(`${urlPoliza}/initialPolicies`);
      commit('setPolicies', data);
   } catch (error) {
      throw error;
   }
};

export const loadPoliciesAsignadas = async ({ rootState, commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlBsa}/getSeguimientosVigentes`,
      );
      commit('setPoliciesAsignadas', data);
   } catch (error) {
      throw error;
   }
};

export const getEstados = async ({ state, commit }) => {
   try {
      if (state.estados && state.estados.length > 0) {
         return state.estados;
      }

      const { data } = await httpInstance.get(`${urlAbm}/estados-unicos`);

      const rawEstados = Array.isArray(data) ? data : [];

      const estados = rawEstados.map((item) => {
         const label =
            item.descripcion || item.estado || item.nombre || String(item.id_estado ?? '');
         return {
            label,
            value: label,
         };
      });

      commit('setEstados', estados);
      return estados;
   } catch (error) {
      throw error;
   }
};

export const getSecciones = async ({ state, commit }) => {
   try {
      if (state.secciones && state.secciones.length > 0) {
         return state.secciones;
      }

      const { data } = await httpInstance.get(`${urlAbm}/seccion/actives`);

      const rawSecciones = Array.isArray(data) ? data : [];

      const secciones = rawSecciones.map((item) => {
         const label =
            item.descripcion || item.nombre || String(item.id_seccion ?? '');
         return {
            label,
            value: label,
         };
      });

      commit('setSecciones', secciones);
      return secciones;
   } catch (error) {
      throw error;
   }
};

export const getProspects = async (state) => {
   try {
      const { data } = await httpInstance.get(`${urlPoliza}/prospects`);
      state.commit(
         'setProspects',
         data.prospects.map((e) => {
            // Mantener las fechas como objetos Date para ordenamiento
            const fechaCreacionDate =
               e.fecha_creacion instanceof Date
                  ? e.fecha_creacion
                  : new Date(e.fecha_creacion);
            const fechaResolucionDate =
               e.fecha_resolucion instanceof Date
                  ? e.fecha_resolucion
                  : new Date(e.fecha_resolucion);

            // Crear strings formateados para visualización
            const fechaCreacionFormatted = formatDateOnly(fechaCreacionDate);
            const fechaResolucionFormatted = formatDateOnly(fechaResolucionDate);

            return {
               ...e,
               cliente: formatClienteNombreCompleto(e, '-'),
               fecha_creacion: fechaCreacionDate, // Objeto Date para ordenamiento
               fecha_resolucion: fechaResolucionDate, // Objeto Date para ordenamiento
               fecha_creacion_display: fechaCreacionFormatted, // String para visualización
               fecha_resolucion_display: fechaResolucionFormatted, // String para visualización
            };
         }),
      );
      state.commit('setSeguimientos', data.seguimientos);
      state.commit('setComentarios', data.comentarios);
   } catch (error) {
      throw error;
   }
};

export const updateStateProspect = async (state, data) => {
   try {
      const response = await httpInstance.post(
         `${urlPoliza}/createEstadoPoliza`,
         data,
      );
      state.commit('updateProspect', response.data);
   } catch (error) {
      throw error;
   }
};

export const updateEstadoPoliza = async (_, estado) => {
   try {
      const { data } = await httpInstance.post(
         `${urlPoliza}/updateEstadoPoliza`,
         estado,
      );
      // data.estado_aplicado puede ser 'PN' (Pendiente de Novedad) si aplica
      // el corte de vinculación con BSA.
      return data;
   } catch (error) {
      throw error;
   }
};
export const getProspectsLowData = async (state) => {
   try {
      const { data } = await httpInstance.get(
         urlPoliza + `/getSolicitudesLowData`,
      );
      const formatData = await data.map((e) => {
         const fechaCreacion = formatDateOnly(e.fecha_creacion);
         return {
            id: e.id_usuario,
            cliente: e.nombre,
            ramo: e.descripcion,
            numero_poliza: e.id_poliza,
            fecha_creacion: fechaCreacion,
            estado: e.id_estado,
            estado_descripcion: e.estado,
            hasAccess: e.hasAccess,
            hash_id: e.hash_id,
            empresa: e.nombre_empresa,
            sucursal: e.nombre_sucursal,
            id_sucursal: e.id_sucursal,
            usuario: e.nombre_usuario,
            capitas: e.capitas,
            hectareas_totales: e.hectareas_totales,
         };
      });
      state.commit('setProspectsTable', formatData);
   } catch (error) {
      throw error;
   }
};

export const getProspectsLowDataPaginated = async (
   { state, commit },
   { reset = false, filtersOverride } = {},
) => {
   try {
      if (reset) {
         commit('resetSolicitudesList', filtersOverride);
      } else if (state.solicitudesList.loading || state.solicitudesList.finished) {
         return;
      }

      commit('setSolicitudesLoading', true);

      const { offset, limit, filters } = state.solicitudesList;

      const formatDateFilter = (value) => {
         if (!value) return undefined;

         // Si ya viene como string YYYY-MM-DD, devolver tal cual
         if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
         }

         const date =
            value instanceof Date
               ? value
               : new Date(value);

         if (Number.isNaN(date.getTime())) {
            return undefined;
         }

         return date.toISOString().slice(0, 10);
      };

      const params = new URLSearchParams();
      params.set('offset', String(offset));
      params.set('limit', String(limit));

      if (filters.cliente) params.set('cliente', filters.cliente);
      if (filters.ramo) params.set('ramo', filters.ramo);
      if (filters.numero) params.set('numero', filters.numero);
      if (filters.usuario) params.set('usuario', filters.usuario);
      if (filters.empresa) params.set('empresa', filters.empresa);
      if (filters.estado) params.set('estado', filters.estado);
      const fechaCreacionDesde = formatDateFilter(filters.fecha_creacion_desde);
      const fechaCreacionHasta = formatDateFilter(filters.fecha_creacion_hasta);

      if (fechaCreacionDesde)
         params.set('fechaCreacionDesde', fechaCreacionDesde);
      if (fechaCreacionHasta)
         params.set('fechaCreacionHasta', fechaCreacionHasta);

      const { data } = await httpInstance.get(
         `${urlPoliza}/getSolicitudesFilteredPaginated?${params.toString()}`,
      );

      if (!data || data.length === 0) {
         commit('setSolicitudesFinished', true);
         commit('setSolicitudesLoading', false);
         return;
      }

      const formatData = data.map((e) => {
         const fechaCreacion = formatDateOnly(e.fecha_creacion);
         return {
            id: e.id_usuario,
            cliente: `${e.nombre} ${e.apellido ?? ''}`,
            ramo: e.descripcion,
            numero_poliza: e.id_poliza,
            fecha_creacion: fechaCreacion,
            estado: e.id_estado,
            estado_descripcion: e.estado,
            hasAccess: e.hasAccess,
            hash_id: e.hash_id,
            empresa: e.nombre_empresa,
            sucursal: e.nombre_sucursal,
            id_sucursal: e.id_sucursal,
            usuario: e.nombre_usuario,
            capitas: e.capitas,
            hectareas_totales: e.hectareas_totales,
         };
      });

      commit('appendSolicitudes', formatData);
      commit('setSolicitudesLoading', false);
   } catch (error) {
      commit('setSolicitudesLoading', false);
      throw error;
   }
};

export const getPoliciesLowData = async (state) => {
   try {
      const { data } = await httpInstance.get(
         urlPoliza + `/getPoliciesLowData`,
      );
      const formatData = await data.map((e) => {
         const fecha_creacion = e.fecha_creacion
            ? new Date(e.fecha_creacion)
            : null;
         const fecha_hasta = e.fecha_hasta ? new Date(e.fecha_hasta) : null;
         const fecha_desde = e.fecha_desde ? new Date(e.fecha_desde) : null;

         const fechaCreacionFormateada = fecha_creacion
            ? fecha_creacion.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         const fechaHastaFormateada = fecha_hasta
            ? fecha_hasta.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         const fechaDesdeFormateada = fecha_desde
            ? fecha_desde.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         return {
            empresa: e.empresa,
            sucursal: e.sucursal,
            usuario: e.usuario,
            id: e.id_usuario,
            cliente: e.nombre,
            id_certificado: e.id_certificado,
            ramo: e.descripcion,
            numero_poliza: e.id_certificado,
            numero_solicitud: e.id_poliza,
            fecha_creacion: fechaCreacionFormateada,
            vigencia_hasta: fechaHastaFormateada,
            vigencia_desde: fechaDesdeFormateada,
            premio: 1,
            estado: e.id_estado,
            estado_descripcion: e.estado,
            suma_asegurada_total: e.suma_asegurada_total,
            hasAccess: e.hasAccess,
            hash_id: e.hash_id,
            hectareas_totales: e.hectareas_totales,
            capitas: e.capitas,
            compania: e.compania,
            id_certificado: e.id_certificado,
            moneda: e.simbolo,
            prima: e.prima,
            premio: e.premio,
            comision_productor: e.comision_productor,
            porcentaje_comision_productor: e.porcentaje_comision_productor,
         };
      });
      state.commit('setProspectsTable', formatData);
   } catch (error) {
      throw error;
   }
};

export const getPoliciesLowDataPaginated = async (
   { state, commit },
   { reset = false, filtersOverride } = {},
) => {
   try {
      if (reset) {
         commit('resetPolizasList', filtersOverride);
      } else if (state.polizasList.loading || state.polizasList.finished) {
         return;
      }

      commit('setPolizasLoading', true);

      const { offset, limit, filters } = state.polizasList;

      const formatDateFilter = (value) => {
         if (!value) return undefined;

         // Si ya viene como string YYYY-MM-DD, devolver tal cual
         if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
         }

         const date =
            value instanceof Date
               ? value
               : new Date(value);

         if (Number.isNaN(date.getTime())) {
            return undefined;
         }

         return date.toISOString().slice(0, 10);
      };

      const params = new URLSearchParams();
      params.set('offset', String(offset));
      params.set('limit', String(limit));

      if (filters.cliente) params.set('cliente', filters.cliente);
      if (filters.ramo) params.set('ramo', filters.ramo);
      if (filters.numero) params.set('numero', filters.numero);
      if (filters.usuario) params.set('usuario', filters.usuario);
      if (filters.empresa) params.set('empresa', filters.empresa);

      const fechaEmisionDesde = formatDateFilter(filters.fecha_desde);
      const fechaVencimientoHasta = formatDateFilter(filters.fecha_hasta);

      if (fechaEmisionDesde)
         params.set('fechaEmisionDesde', fechaEmisionDesde);
      if (fechaVencimientoHasta)
         params.set('fechaVencimientoHasta', fechaVencimientoHasta);

      const { data } = await httpInstance.get(
         `${urlPoliza}/getPoliciesFilteredPaginated?${params.toString()}`,
      );

      if (!data || data.length === 0) {
         commit('setPolizasFinished', true);
         commit('setPolizasLoading', false);
         return;
      }

      const formatData = data.map((e) => {
         const fecha_creacion = e.fecha_creacion
            ? new Date(e.fecha_creacion)
            : null;
         const fecha_hasta = e.fecha_hasta ? new Date(e.fecha_hasta) : null;
         const fecha_desde = e.fecha_desde ? new Date(e.fecha_desde) : null;

         const fechaCreacionFormateada = fecha_creacion
            ? fecha_creacion.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         const fechaHastaFormateada = fecha_hasta
            ? fecha_hasta.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         const fechaDesdeFormateada = fecha_desde
            ? fecha_desde.toLocaleDateString('es-ES', {
               year: 'numeric',
               month: '2-digit',
               day: '2-digit',
               timeZone: 'UTC',
            })
            : 'Fecha no disponible';

         return {
            empresa: e.empresa,
            sucursal: e.sucursal,
            usuario: e.usuario,
            id: e.id_usuario,
            cliente: `${e.nombre} ${e.apellido ?? ''}`,
            id_certificado: e.id_certificado,
            ramo: e.descripcion,
            numero_poliza: e.id_certificado,
            numero_solicitud: e.id_poliza,
            fecha_creacion: fechaCreacionFormateada,
            vigencia_hasta: fechaHastaFormateada,
            vigencia_desde: fechaDesdeFormateada,
            premio: 1,
            estado: e.id_estado,
            estado_descripcion: e.estado,
            suma_asegurada_total: e.suma_asegurada_total,
            hasAccess: e.hasAccess,
            hash_id: e.hash_id,
            hectareas_totales: e.hectareas_totales,
            capitas: e.capitas,
            compania: e.compania,
            id_certificado: e.id_certificado,
            moneda: e.simbolo,
            prima: e.prima,
            premio: e.premio,
            comision_productor: e.comision_productor,
            porcentaje_comision_productor: e.porcentaje_comision_productor,
         };
      });

      commit('appendPolizas', formatData);
      commit('setPolizasLoading', false);
   } catch (error) {
      commit('setPolizasLoading', false);
      throw error;
   }
};

export const addLote = async ({ rootState, commit, state, dispatch }, data) => {
   const allLocalidades = Object.values(rootState.main.localidades).flat();

   const descripcionLocalidad = allLocalidades.find(
      (l) => l.id_localidad === data.id_localidad,
   ) || { descripcion: '', codigo_postal: '' };
   const descripcionProvincia = rootState.main.provincias.find(
      (provincia) => provincia.id_provincia === data.id_provincia,
   )?.nombre;
   let departamentosPorProvincia =
      rootState.main.departamentos[data.id_provincia] || [];
   if (departamentosPorProvincia.length === 0) {
      await dispatch('main/getDepartamentosById', data.id_provincia, {
         root: true,
      });
      departamentosPorProvincia =
         rootState.main.departamentos[data.id_provincia] || [];
   }

   const descripcionDepartamento = departamentosPorProvincia.find(
      (departamento) => departamento.id_departamento === data.id_departamento,
   )?.descripcion;
   commit('addLote', {
      ...data,
      descripcionLocalidad: descripcionLocalidad.descripcion,
      codigo_postal: descripcionLocalidad.codigo_postal,
      descripcionProvincia,
      descripcionDepartamento,
   });
};

export const editLote = async ({ rootState, commit, dispatch }, data) => {
   const descripcionProvincia = rootState.main.provincias.find(
      (provincia) => provincia.id_provincia === data.id_provincia,
   );
   let departamentos = rootState.main.departamentos[data.id_provincia] || [];
   if (departamentos.length === 0 && data.id_provincia) {
      await dispatch('main/getDepartamentosById', data.id_provincia, {
         root: true,
      });
      departamentos = rootState.main.departamentos[data.id_provincia] || [];
   }

   const descripcionDepartamento = departamentos.find(
      (departamento) => departamento.id_departamento === data.id_departamento,
   );

   const key = `${data.id_provincia}_${data.id_departamento}`;
   const localidades = rootState.main.localidades[key] || [];

   const descripcionLocalidad =
      localidades.find(
         (localidad) => localidad.id_localidad === data.id_localidad,
      ) || {};

   commit('editLote', {
      ...data,
      id_localidad: descripcionLocalidad.id_localidad ?? data.id_localidad,
      descripcionLocalidad:
         descripcionLocalidad.descripcion ?? data.descripcionLocalidad,
      codigo_postal: descripcionLocalidad.codigo_postal ?? data.codigo_postal,
      descripcionProvincia:
         descripcionProvincia?.nombre ?? data.descripcionProvincia,
      descripcionDepartamento:
         descripcionDepartamento?.descripcion ?? data.descripcionDepartamento,
      id_departamento:
         descripcionDepartamento?.id_departamento ?? data.id_departamento,
      id_provincia: descripcionProvincia?.id_provincia ?? data.id_provincia,
   });
};

export const createLote = async ({ state, rootState }) => {
   try {
      const lotes = state.lotes.map((e) => {
         return {
            nombre: e.nombre || '',
            codigo_postal: e.codigo_postal || 0,
            id_moneda: e.moneda || 1,
            hectareas: e.hectareas || 0,
            id_cultivo: e.cultivo.id_cultivo || 0,
            id_campania: e.cultivo.id_campania || 0,
            viento: e.viento || false,
            planchado: e.planchado || false,
            helada: e.helada || false,
            incendio_rastrojo: e.incendio_rastrojo || false,
            id_lote: e.id,
            id_departamento: e.id_departamento || 0,
            id_provincia: e.id_provincia || 0,
            id_localidad: e.id_localidad || 0,
            suma_asegurada_total: e.suma_asegurada_total || 0,
            suma_asegurada_hectarea: e.suma_asegurada_hectarea || 0,
            cotizacion: e.cultivo.valor_referencial || 0,
            qq_hectarea: e.qq_hectarea || 0,
         };
      });
      const poliza = state.poliza;
      const { data } = await httpInstance.post(`${urlFlujoAgro}/createLote`, {
         poliza: {
            id_poliza: poliza.id_poliza,
            id_cliente: poliza.id_cliente,
            id_seccion: poliza.id_seccion,
         },
         lotes,
      });
      return data;
   } catch (error) {
      throw error;
   }
};

export const getProspect = async ({ commit }, id_poliza) => {
   try {
      const { data } = await httpInstance.get(
         `${urlBsa}/getSeguimientoVigente`,
         { id_poliza },
      );

      const {
         estados,
         lotes,
         historialEstados,
         vehiculo,
         locaciones,
         documentos,
         referente,
         ...policyData
      } = data;
      policyData.posibilidad_cierre = data.art_poliza?.posibilidad_cierre ?? data.posibilidad_cierre ?? null;
      if (referente) {
         policyData.referente = referente;
         commit('cliente/setReferente', referente, { root: true });
      }
      commit('bsa/setEstados', estados, { root: true });
      const clienteData = {
         id_cliente: data.id_cliente,
         email: data.email,
         nombre: data.nombre,
         cuit: data.cuit,
         fechaNacimiento: data.fecha_nacimiento ?? null,
         actividad: data.actividad ?? null,
         iibb: data.iibb ?? null,
      };

      commit('cliente/setCliente', clienteData, { root: true });

      if (Number(policyData.id_seccion) === 8) {
         commit('hipoteca/setLocaciones', locaciones ?? [], { root: true });
         commit('hipoteca/setDocumentos', documentos ?? [], { root: true });
      }

      if (Number(policyData.id_seccion) === 9) {
         commit(
            'responsabilidadCivil/setDatos',
            data.responsabilidad_civil ?? null,
            { root: true },
         );
         commit('responsabilidadCivil/setDocumentos', documentos ?? [], {
            root: true,
         });
      }

      // Si vienen lotes, los mando a su mutación
      if (lotes && Array.isArray(lotes)) {
         commit('setLotes', lotes);
      }

      // Agregar historialEstados al policyData
      if (historialEstados && Array.isArray(historialEstados)) {
         policyData.historialEstados = historialEstados;
      }

      if (vehiculo) {
         commit('automotor/setVehiculo', vehiculo, { root: true });
      }
      commit('setPolicy', policyData);
   } catch (error) {
      throw error;
   }
};

/** Misma lógica que getProspect: llama a getSeguimientoVigente API y distribuye poliza, cliente, vehículo mapeado. Acepta payload como id o { id_poliza }. */
export const getSeguimientoVigente = async (context, payload) => {
   const id_poliza = payload != null && typeof payload === 'object' && 'id_poliza' in payload ? payload.id_poliza : payload;
   return getProspect(context, id_poliza);
};

export const putPayMethod = async ({ state, commit }, dataSend) => {
   try {
      const { data } = await httpInstance.post(
         `${urlFlujoAgro}/payMethod`,
         dataSend,
      );
      commit('updatePol', { key: 'id_forma_pago', value: data.id_forma_pago });
   } catch (error) {
      throw error;
   }
};

export const putMapas = async ({ state, commit }, dataSend) => {
   try {
      const { data } = await httpInstance.put(
         `${urlFlujoAgro}/putMapas`,
         dataSend,
      );
      return data;
   } catch (error) {
      throw error;
   }
};
export const putMapasSolicitud = async ({ state, commit }, dataSend) => {
   try {
      const { data } = await httpInstance.put(
         `${urlFlujoAgro}/putMapasSolicitud`,
         dataSend,
      );
      return data;
   } catch (error) {
      throw error;
   }
};

export const putPlanSelected = async ({ state, commit }, data2) => {
   try {
      const { data } = await httpInstance.put(`${urlFlujoAgro}/planSelected`, {
         id_poliza: state.poliza.id_poliza,
         id_compania: data2.id_compania,
         lotes: data2.lotes,
      });
      return data;
   } catch (error) {
      throw error;
   }
};

export const getLocalidad = async ({ commit }, id_localidad) => {
   try {
      const { data } = await httpInstance.get(`${urlFlujoArt}/getLocalidad`, {
         id_localidad,
      });
      commit('setPolicy', data);
   } catch (error) {
      throw error;
   }
};

export const loadPolizaGranizo = async (_, formData) => {
   const { getAuthHeaders } = useAuthHeaders();
   const { ok, config, message } = getAuthHeaders();
   if (!ok) {
      return { ok: false, message };
   }

   try {
      const { data } = await httpInstance.post(
         `${urlPoliza}/extractPolizaData`,
         formData,
         true,
      );
      return data;
   } catch (error) {
      throw error;
   }
};

export const setResumenPorMonedaCultivo = (
   { commit },
   { resumen, compania },
) => {
   commit('setResumenPorMonedaCultivo', { resumen, compania });
};
export const updateVendedor = async (
   { commit },
   { id_poliza, id_empresa, id_usuario, fecha_creacion },
) => {
   try {
      const response = await httpInstance.put(`${urlPoliza}/updateVendedor`, {
         id_poliza,
         id_empresa,
         id_usuario,
         fecha_creacion,
      });
      commit('updatePol', { key: 'empresavendedor', value: id_empresa });
      commit('updatePol', { key: 'id_usuario', value: id_usuario });
   } catch (error) {
      throw error;
   }
};

export const finalizarPoliza = async (_, data) => {
   try {
      const response = await httpInstance.post(
         `${urlPoliza}/finalizarPoliza`,
         data,
      );
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const logAsignarPoliza = async (_, data) => {
   try {
      const response = await httpInstance.post(
         `${urlBsa}/logAsignarPoliza`,
         data,
      );
   } catch (error) {
      throw error;
   }
};

export const getLotesByPoliza = async ({ state, commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAgro}/lotesByPoliza?id_poliza=${state.poliza.hash_id}`,
      );
      commit('setLotes', data);
      return data;
   } catch (error) {
      throw error;
   }
};

export const altaCliente = async ({ commit, rootState, rootGetters }) => {
   try {
      const { cliente, referente } = rootState.cliente;
      const { poliza } = rootState.poliza;
      const { usuario } = rootState.auth;
      const datosVehiculo = rootGetters['automotor/datosVehiculoProspect'];
      const usaPosibilidadCierre = seccionUsaPosibilidadCierre(
         poliza.id_seccion,
      );

      const res = await httpInstance.post(`${urlPoliza}/createProspect`, {
         data: {
            cliente: {
               cuit: cliente.cuit,
               email: cliente.email,
               domicilio: cliente.domicilio,
               id_condicion_fiscal: cliente.id_condicion_fiscal,
               codigo_postal: cliente.codigo_postal,
               nombre:
                  cliente.tipo_persona === 1
                     ? cliente.nombre
                     : cliente.razonSocial,
               apellido: cliente.apellido,
               telefono: cliente.telefono,
               tipo_persona: cliente.tipo_persona,
               fecha_nacimiento: cliente.fechaNacimiento
                  ? String(cliente.fechaNacimiento).replace(/\//g, '-')
                  : null,
               sexo: cliente.sexo ?? null,
            },
            referente: {
               cuit: referente.cuit,
               email: referente.email,
               nombre: referente.nombre,
               telefono: referente.telefono,
               observaciones: referente.observaciones,
            },
            poliza: {
               id_provincia: Number(poliza.id_provincia),
               id_localidad: Number(poliza.id_localidad),
               id_seccion: Number(poliza.id_seccion), // si aca se agregan mas datos, hay que agregarlos en la interfaz del back
               hash_id: poliza.hash_id,
               ...(usaPosibilidadCierre
                  ? { posibilidad_cierre: poliza.posibilidad_cierre }
                  : {}),
            },
            vehiculo: datosVehiculo,
         },
         usuario: {
            id_usuario: poliza.id_usuario ?? usuario.id_usuario ?? 1,
         },
      });

      // Actualizar el store con los datos del prospecto creado
      if (res.data?.prospecto) {
         commit(
            'poliza/updatePol',
            {
               key: 'id_poliza',
               value: res.data.prospecto,
            },
            { root: true },
         );
         commit(
            'poliza/updatePol',
            {
               key: 'id_cliente',
               value: res.data.id_cliente,
            },
            { root: true },
         );
         commit(
            'poliza/updatePol',
            {
               key: 'hash_id',
               value: res.data.hash_id,
            },
            { root: true },
         );
         commit(
            'cliente/updateCli',
            {
               key: 'id_cliente',
               value: res.data.id_cliente,
            },
            { root: true },
         );
      }

      return res.data;
   } catch (error) {
      throw error;
   }
};
